// For reasons unknown, these exported objects must live in a separate module
// than in `./Routes.tsx` or `./Island/Router`. Otherwise, test failures will
// occur specifically under `src/ui/MarkdownEditor`.

import type { MyDevelopersRouteParams } from '@core/types/metrics';

/**
 * Dictionary of all browser router paths that are defined segmented by actions
 * being performed like `view` or `edit`.
 */
export const SuperHubRoutePaths = {
  viewGuidePage: '/:section(docs)/:slug?',
  viewReferencePage: '/:section(reference)/:slug?',
  viewCustomPage: '/:section(page)/:slug?',
  viewChangelog: '/:section(changelog)/:slug?',
  viewDiscussions: '/:section(discuss)(-new)?/:slug?',
  viewRecipes: '/:section(recipes)/:slug?',
  viewGraphQL: '/:section(graphql)',
  editPage: '/:action(update)/:section(changelog|docs|page|reference|recipes)/:slug?',
  reviewPage:
    '/:action(review)/:section(api_definitions|custom_components|docs|page|recipes|reference|reusable_content)?/:slug?',
  createGuidePage: '/:action(create)/:section(docs)/:routeParentCategory/:routeParentPage?',
  createReferencePage: '/:action(create)/:section(reference)/:routeParentCategory/:routeParentPage?',
  createCustomPage: '/:action(create)/:section(page)',
  createChangelog: '/:action(create)/:section(changelog)',
  comparePage: '/:action(compare)/:section(changelog|docs|page|reference)/:slug/:commitHash?',
  oauth2Redirect: '/oauth2-redirect',
} as const;

/**
 * Dictionary of all hash router paths that are defined, segmented by actions
 * being performed like create, settings, etc.
 */
export const SuperHubHashRoutePaths = {
  settings: '/:action(settings|appearance)/:section*',
  branches: '/:action(branches)',
  myDevelopers: '/:action(my-developers)/:type*',
  docsAudit: '/:action(docs-audit)/:auditId?',
} as const;

/**
 * Browser route params as defined by the docs routes inside ../../Hub.
 */
export interface SuperHubRouteParams {
  /**
   * Action being performed on a particular section.
   * - `compare`: when viewing page history
   * - `create`: when creating a new page
   * - `review`: when reviewing changes within a branch
   * - `update`: when editing a page
   * @example /create/page -> create a new custom page
   */
  action?: 'compare' | 'create' | 'review' | 'update';
  /**
   * During `compare` actions, contains the git commit hash to diff against.
   * @example /compare/docs/getting-started/75a2919zdz
   */
  commitHash?: string;
  /**
   * During `create` actions for docs and reference sections only, indicates the
   * parent category `title` for the new page being created.
   * @example /create/docs/Documentation
   */
  routeParentCategory?: string;
  /**
   * During `create` actions for docs and reference sections only, indicates the
   * parent page `slug` for the new sub-page being created.
   * @example /create/docs/Documentation/how-to-fly
   */
  routeParentPage?: string;
  /**
   * Documentation section currently being viewed or acted upon.
   */
  section?:
    | 'api_definitions'
    | 'changelog'
    | 'custom_components'
    | 'discuss'
    | 'docs'
    | 'home'
    | 'page'
    | 'recipes'
    | 'reference'
    | 'reusable_content';
  /**
   * Unique document slug that is currently being viewed or acted upon.
   */
  slug?: string;
}

/**
 * Maps route section names (e.g. `docs`, `reference`) to the corresponding
 * project store navigation link keys (e.g. `guides`, `custom_pages`). These
 * keys match both `appearance.navigation.links` and the sidebar status endpoint.
 */
export const routeSectionToNavLinkKey: Record<string, string> = {
  docs: 'guides',
  reference: 'reference',
  recipes: 'recipes',
  changelog: 'changelog',
  discuss: 'discussions',
  page: 'custom_pages',
};

export interface SuperHubHashRouteParams {
  /**
   * Top-level action that is currently being performed. This is the initial
   * segment of the route that describes what mode we are in.
   * - `ai`: When accessing AI features like AI Assistant, Content Generator, etc.
   * - `appearance`: When configuring project settings with live-preview forms
   * for things like the theme editor and custom CSS/JS.
   * - `branches`: When viewing the project Branches page (top-level — lives
   * outside `settings/*` since branches is a primary surface, not a setting).
   * - `settings`: When configuring project settings like content, authentication,
   * team members, etc.
   * - 'content': When managing content like reusable content, glosary, etc.
   * - `docs-audit`: When viewing the AI Docs Audit page for a project.
   * - `my-developers`: When viewing metrics and developer data.
   */
  action?: 'ai' | 'appearance' | 'branches' | 'content' | 'docs-audit' | 'my-developers' | 'settings';

  /**
   * Currently only used in docs-audit routes to identify an audit
   */
  id?: string;

  /**
   * MyDevelopers specific subroute type
   */
  identifier?: MyDevelopersRouteParams['identifier'];

  /**
   * Top-level section within a particular action.
   */
  section?: string;

  /**
   * MyDevelopers specific subroute type
   */
  type?: MyDevelopersRouteParams['type'];
}
